import { IssueType, IssueTypeEnum } from '../issue-source';
import { IssueStatusEnum, IssueStatusType } from '../issue-status.enum';
import { ROLE, Role } from '../role';
import type {
  ActionWorkflowField,
  AuditFindingField,
  AuditIssueFieldValue,
  BasicIssueField,
  ControlDeficiencyField,
  ControlDeficiencyIssueFieldValue,
  IssueFieldValue,
  RelationshipField,
} from './field-types';

export const BASIC_ISSUE_FIELDS = {
  CODE: 'code',
  ISSUE_TYPE: 'issueType',
  ISSUE_SUBTYPE: 'issueSubtype',
  TITLE: 'title',
  DESCRIPTION: 'description',
  IMPACT: 'impact',
  ATTACHMENTS: 'attachments',
  TIMELINE_EVENTS: 'timelineEvents',
  DUE_DATE: 'dueDate',
  SEVERITY: 'severity',
  STATUS: 'status',
  ACTUAL_REMEDIATION_DATE: 'actualRemediationDate',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  ISSUE_REPORTER: 'creator',
  ISSUE_OWNER: 'owner',
  ISSUE_APPROVER: 'approver',
  ISSUE_WATCHERS: 'watchers',
  RELATED_ORG_UNIT: 'govmapGroups',
} as const;

export const ACTION_WORKFLOW_FIELDS = {
  EXECUTION_TYPE: 'executionType',
  ACTION_PROGRESS: 'actionProgress',
  ACTION_COUNT: 'actionCount',
  IS_INITIATED: 'isInitiated',
  ACTIONS: 'actions',
} as const;

export const AUDIT_FINDING_EXTRA_FIELDS = {
  CLASSIFICATION: 'classification',
  ROOT_CAUSE: 'rootCause',
  RECOMMENDATIONS: 'recommendations',
  MANAGEMENT_RESPONSE: 'managementResponse',
  RESOLUTION: 'resolution',
  REVIEW_STATUS: 'reviewStatus',
  INCLUDE_IN_AUDIT_REPORT: 'includeInAuditReport',
  DATE_PUBLISHED: 'datePublished',
} as const;

export const CONTROL_DEFICIENCY_EXTRA_FIELDS = {
  CLASSIFICATION: 'classification',
  ROOT_CAUSE: 'rootCause',
  MANAGEMENT_RESPONSE: 'managementResponse',
  RESOLUTION: 'resolution',
  REMEDIATION: 'remediation',
} as const;

export const SLA_FIELDS = {
  SLA_STATUS: 'slaStatus',
  SLA_DAYS: 'slaDays',
  SLA_DUE_DAYS: 'slaDueDays',
  SLA_ENABLED: 'slaEnabled',
  MAX_DUE_DATE: 'maxDueDate',
  ESCALATION_DAYS: 'escalationDays',
  REMINDER_DAYS: 'reminderDays',
} as const;

export const RELATIONSHIP_FIELDS = {
  RELATIONSHIP_COUNT: 'relationshipCount',
} as const;

export const ISSUE_FIELDS = {
  ...BASIC_ISSUE_FIELDS,
  ...ACTION_WORKFLOW_FIELDS,
  ...SLA_FIELDS,
  ...RELATIONSHIP_FIELDS,
  ...AUDIT_FINDING_EXTRA_FIELDS,
  ...CONTROL_DEFICIENCY_EXTRA_FIELDS,
} as const;

export const basicFields: BasicIssueField[] = Object.values(BASIC_ISSUE_FIELDS);
export const actionWorkflowFields: ActionWorkflowField[] = Object.values(
  ACTION_WORKFLOW_FIELDS,
);
export const relationshipFields: RelationshipField[] =
  Object.values(RELATIONSHIP_FIELDS);
export const auditFindingExtraFields: AuditFindingField[] = Object.values(
  AUDIT_FINDING_EXTRA_FIELDS,
);
export const controlDeficiencyExtraFields: ControlDeficiencyField[] =
  Object.values(CONTROL_DEFICIENCY_EXTRA_FIELDS);

export const auditFindingFields: readonly AuditIssueFieldValue[] = [
  ...basicFields,
  ...auditFindingExtraFields,
] as const;

export const controlDeficiencyFields: readonly ControlDeficiencyIssueFieldValue[] =
  [...basicFields, ...controlDeficiencyExtraFields] as const;

export const basicFieldsWithActionWorkflow: readonly IssueFieldValue[] = [
  ...basicFields,
  ...relationshipFields,
  ...actionWorkflowFields,
] as const;

export const auditFindingFieldsWithActionWorkflow: readonly AuditIssueFieldValue[] =
  [
    ...basicFields,
    ...relationshipFields,
    ...actionWorkflowFields,
    ...auditFindingExtraFields,
  ] as const;

// eslint-disable-next-line max-len
export const controlDeficiencyFieldsWithActionWorkflow: readonly ControlDeficiencyIssueFieldValue[] =
  [
    ...basicFields,
    ...relationshipFields,
    ...actionWorkflowFields,
    ...controlDeficiencyExtraFields,
  ] as const;

const earlyRoles: readonly Role[] = [
  ROLE.ISSUE_TYPE_MANAGER,
  ROLE.CREATOR,
  ROLE.WATCHER,
] as const;

const issueRoles: readonly Role[] = [
  ROLE.ISSUE_TYPE_MANAGER,
  ROLE.CREATOR,
  ROLE.WATCHER,
  ROLE.OWNER,
  ROLE.APPROVER,
] as const;

const issueRolesWithoutApprover: readonly Role[] = [
  ROLE.ISSUE_TYPE_MANAGER,
  ROLE.CREATOR,
  ROLE.WATCHER,
  ROLE.OWNER,
] as const;

export const allRoles: readonly Role[] = [
  ROLE.ISSUE_TYPE_MANAGER,
  ROLE.CREATOR,
  ROLE.OWNER,
  ROLE.APPROVER,
  ROLE.WATCHER,
  ROLE.ACTION_OWNER,
  ROLE.ACTION_APPROVER,
] as const;

type IssueReadableFieldsByType = {
  [IssueTypeEnum.ISSUE]: Partial<
    Record<
      IssueStatusType,
      { roles: readonly Role[]; fields: readonly IssueFieldValue[] }
    >
  >;
  [IssueTypeEnum.EXCEPTION]: Partial<
    Record<
      IssueStatusType,
      { roles: readonly Role[]; fields: readonly IssueFieldValue[] }
    >
  >;
  [IssueTypeEnum.INCIDENT]: Partial<
    Record<
      IssueStatusType,
      { roles: readonly Role[]; fields: readonly IssueFieldValue[] }
    >
  >;
  [IssueTypeEnum.AUDIT_FINDING]: Partial<
    Record<
      IssueStatusType,
      { roles: readonly Role[]; fields: readonly AuditIssueFieldValue[] }
    >
  >;
  [IssueTypeEnum.CONTROL_DEFICIENCY]: Partial<
    Record<
      IssueStatusType,
      {
        roles: readonly Role[];
        fields: readonly ControlDeficiencyIssueFieldValue[];
      }
    >
  >;
};

const basicIssueTypeReadableConfig = {
  [IssueStatusEnum.DRAFT]: {
    roles: earlyRoles,
    fields: basicFieldsWithActionWorkflow,
  },
  [IssueStatusEnum.IN_REVIEW]: {
    roles: earlyRoles,
    fields: basicFieldsWithActionWorkflow,
  },
  [IssueStatusEnum.IN_PROGRESS]: {
    roles: allRoles,
    fields: basicFieldsWithActionWorkflow,
  },
  [IssueStatusEnum.TO_BE_APPROVED]: {
    roles: allRoles,
    fields: basicFieldsWithActionWorkflow,
  },
  [IssueStatusEnum.CLOSED]: {
    roles: allRoles,
    fields: basicFieldsWithActionWorkflow,
  },
  [IssueStatusEnum.DISCARDED]: {
    roles: allRoles,
    fields: basicFieldsWithActionWorkflow,
  },
} as const satisfies Partial<
  Record<
    IssueStatusType,
    { roles: readonly Role[]; fields: readonly IssueFieldValue[] }
  >
>;

export const ISSUE_READABLE_FIELDS: IssueReadableFieldsByType = {
  [IssueTypeEnum.ISSUE]: basicIssueTypeReadableConfig,
  [IssueTypeEnum.EXCEPTION]: basicIssueTypeReadableConfig,
  [IssueTypeEnum.INCIDENT]: basicIssueTypeReadableConfig,
  [IssueTypeEnum.AUDIT_FINDING]: {
    [IssueStatusEnum.DRAFT]: {
      roles: earlyRoles,
      fields: auditFindingFieldsWithActionWorkflow,
    },
    [IssueStatusEnum.IN_REVIEW]: {
      roles: earlyRoles,
      fields: auditFindingFieldsWithActionWorkflow,
    },
    [IssueStatusEnum.TO_BE_PUBLISHED]: {
      roles: issueRoles,
      fields: auditFindingFieldsWithActionWorkflow,
    },
    [IssueStatusEnum.PENDING_ACCEPTANCE]: {
      roles: issueRolesWithoutApprover,
      fields: auditFindingFieldsWithActionWorkflow,
    },
    [IssueStatusEnum.IN_PROGRESS]: {
      roles: allRoles,
      fields: auditFindingFieldsWithActionWorkflow,
    },
    [IssueStatusEnum.TO_BE_APPROVED]: {
      roles: allRoles,
      fields: auditFindingFieldsWithActionWorkflow,
    },
    [IssueStatusEnum.CLOSED]: {
      roles: allRoles,
      fields: auditFindingFieldsWithActionWorkflow,
    },
    [IssueStatusEnum.DISCARDED]: {
      roles: allRoles,
      fields: auditFindingFieldsWithActionWorkflow,
    },
  },
  [IssueTypeEnum.CONTROL_DEFICIENCY]: {
    [IssueStatusEnum.DRAFT]: {
      roles: earlyRoles,
      fields: controlDeficiencyFieldsWithActionWorkflow,
    },
    [IssueStatusEnum.IN_REVIEW]: {
      roles: earlyRoles,
      fields: controlDeficiencyFieldsWithActionWorkflow,
    },
    [IssueStatusEnum.IN_PROGRESS]: {
      roles: allRoles,
      fields: controlDeficiencyFieldsWithActionWorkflow,
    },
    [IssueStatusEnum.TO_BE_APPROVED]: {
      roles: allRoles,
      fields: controlDeficiencyFieldsWithActionWorkflow,
    },
    [IssueStatusEnum.CLOSED]: {
      roles: allRoles,
      fields: controlDeficiencyFieldsWithActionWorkflow,
    },
    [IssueStatusEnum.DISCARDED]: {
      roles: allRoles,
      fields: controlDeficiencyFieldsWithActionWorkflow,
    },
  },
} as const;

export interface FlattenedIssueReadableField {
  type: IssueType;
  status: IssueStatusType;
  role: Role;
  fields: readonly IssueFieldValue[];
}

export const ISSUE_READABLE_FIELDS_FLATTENED: readonly FlattenedIssueReadableField[] =
  Object.entries(ISSUE_READABLE_FIELDS).flatMap(([type, statuses]) =>
    Object.entries(statuses).flatMap(([status, config]) => {
      if (!config) return [];

      return config.roles.map((role) => ({
        type: type as IssueType,
        status: status as IssueStatusType,
        role,
        fields: config.fields,
      }));
    }),
  );
